import { useQuery, useMutation } from "@tanstack/react-query";
import * as api from "./request";
import { getUserDetails } from "./auth";
import * as auth from "./auth";
import { useGetUserCompanies } from "./query";
import { FULL_USER_COMPANIES_PAYLOAD } from "./common/noticeManagementUtils";

const BASE_URL = "/api/v1";

export { FULL_USER_COMPANIES_PAYLOAD };

// License Applications with role-based filtering
export function useLicenseApplications(filters?: any, enabled = true) {
  const user = getUserDetails();
  const role = auth.getUserRole();

  let queryParams = { ...filters };

  if (role === "License Watcher") {
    queryParams.watcher_id = user.user_id;
  } else if (role === "License Owner") {
    queryParams.owner_or_editor_id = user.user_id;
  }
  // License Manager sees all (no filter)

  const { data, isFetching, refetch } = useQuery(
    ["licenseApplications", queryParams],
    async () =>
      await api.get(`${BASE_URL}/license/license_applications`, queryParams),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled,
    }
  );

  var items = (data && data.data && data.data.items) ? data.data.items : [];
  var meta = (data && data.data && data.data.meta) ? data.data.meta : {};

  return {
    applications: items,
     total: (meta && meta.total_count) || 0,
    isFetching,
    refetch,
  };
}

// Get all license applications (with auto-filtering by user's companies)
export function useGetLicenseApplications(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;
  const payloadFilters = (payload && payload.filters) || {};

  const enhancedPayload = hasCompanies ? {
    ...(payload || {}),
    filters: {
      ...payloadFilters,
      ...(payloadFilters.company_id_eq ? {} : { company_id_in: companyIds }),
      // ...(payloadFilters.associate_company_id_eq ? {} : { associate_company_id_in: Array.from(associateCompanyIds) }),
      // ...(payloadFilters.location_id_eq ? {} : { location_id_in: Array.from(locationIds) }),
    }
  } : (payload || {});
  const { data, isFetching, refetch } = useQuery(
    ["allLicenseApplications", enhancedPayload],
    async () =>
      await api.get(`${BASE_URL}/license/license_applications`, enhancedPayload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && hasCompanies,
    }
  );
  var items = !hasCompanies ? [] : ((data && data.data && data.data.items) ? data.data.items : []);
  var meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  return {
    applications: items,
    total: ((meta && meta.total_count) || 0),
    isFetching: isFetching || isLoadingCompanies,
    refetch,
    companyIds,
  };
}

export function useGetCompanyLicenseApplications(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const { data, isFetching, refetch } = useQuery(
    ["allLicenseApplications"],
    async () =>
      await api.get(`${BASE_URL}/license/license_applications`, payload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled,
    }
  );
  var items = ((data && data.data && data.data.items) ? data.data.items : []);
  var meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  return {
    applications: items,
    total: ((meta && meta.total_count) || 0),
    isFetching: isFetching || isLoadingCompanies,
    refetch,
  };
}

// Get single license application
export function useGetLicenseApplication(id: string, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ["licenseApplication", id],
    async () => await api.get(`${BASE_URL}/license/license_applications/${id}`),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!id && enabled,
    }
  );
  return {
    application: (data || {}).data || {},
    isFetching,
    refetch,
  };
}

// Create license application
export function useCreateLicenseApplication(onSuccess?: any, onError?: any) {
  const {
    mutate: createApplication,
    error,
    isLoading: creating,
  } = useMutation(
    ["createLicenseApplication"],
    async (payload) =>
      await api.post(`${BASE_URL}/license/license_applications`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
      ? response.data
      : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );
  return { createApplication, error, creating };
}

export function useGetApplicationTypeDocuments(
    applicationTypeId: string | undefined,
    actionType: string | undefined,
    enabled = true
  ) {
    const payload = {
      filters: {
        application_type_id_eq: applicationTypeId,
        is_active_eq: true
      },
      include: [
       "document_type",
      ],
      includes: [
       "document_type",
      ],
    };

    const { data, isFetching, refetch } = useQuery(
      ["applicationTypeDocuments", applicationTypeId, actionType],
      async () =>
        await api.get(`${BASE_URL}/license/application_type_documents`, payload),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!applicationTypeId && !!actionType,
      }
    );

    var items = (data && data.data && data.data.items) ? data.data.items : [];
    var meta = (data && data.data && data.data.meta) ? data.data.meta : {};

    return {
      applicationTypeDocuments: items,
      total: (meta && meta.total_count) || 0,
      isFetching,
      refetch,
    };
  }

// License Application Documents
 export function useGetLicenseApplicationDocuments(
    license_application_id: string,
    enabled = true
  ) {
    const payload = {
      include: ["application_type_document", "asset"],
      includes: ["application_type_document", "asset"],
      filters: {
        license_application_id_eq: license_application_id,
      },
    };

    const { data, isFetching, refetch } = useQuery(
      ["license_application_documents", license_application_id],
      async () => await api.get(`${BASE_URL}/license/license_application_documents`, payload),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!license_application_id,
      }
    );

    var items = (data && data.data && data.data.items) ? data.data.items : [];
    var meta = (data && data.data && data.data.meta) ? data.data.meta : {};

    return {
      licenseApplicationDocuments: items,
      total: (meta && meta.total_count) || 0,
      isFetching,
      refetch,
    };
  }

export function useGetLicenseApplicationHistory(
    licenseApplicationId: string | undefined,
    fields?: string,
    enabled = true
  ) {
    const payload = fields ? { fields: fields } : {};

    const { data, isFetching, refetch } = useQuery(
      ["licenseApplicationHistory", licenseApplicationId],
      async () =>
        await api.get(
          `${BASE_URL}/license/license_applications/${licenseApplicationId}/history`,
          payload
        ),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!licenseApplicationId,
      }
    );

  const historyItems = data && data.data ? data.data : [];

  return {
    historyItems,
    isFetching,
    refetch,
  };
}


export function useGetCompanyLicenseHistory(
    CompanyLicenseId: string | undefined,
    fields?: string,
    enabled = true
  ) {
    const payload = fields ? { fields: fields } : {};

    const { data, isFetching, refetch } = useQuery(
      ["companyLicenseHistory", CompanyLicenseId],
      async () =>
        await api.get(
          `${BASE_URL}/license/company_licenses/${CompanyLicenseId}/history`,
          payload
        ),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!CompanyLicenseId,
      }
    );

  const historyItems = data && data.data ? data.data : [];

  return {
    historyItems,
    isFetching,
    refetch,
  };
}

// Create License Application Document
export function useCreateLicenseApplicationDocument(onSuccess?: any, onError?: any) {
  const {
    mutate: createLicenseApplicationDocument,
    error,
    isLoading: creating,
  } = useMutation(
    ["createLicenseApplicationDocument"],
    async (payload: FormData) =>
      await api.post(`${BASE_URL}/license/license_application_documents`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );
  return { createLicenseApplicationDocument, error, creating };
}

// Update license application
export function useUpdateLicenseApplication(onSuccess?: any, onError?: any) {
    const { mutate: updateApplication, error, isLoading: updating } = useMutation(
      ["updateLicenseApplication"],
      async (payload: any) => {
        // Check if this is a file upload (FormData)
        if (payload.isFileUpload && payload.formData) {
          // Send FormData with multipart/form-data
          return await api.put(
            `${BASE_URL}/license/license_applications/${payload.id}`,
            payload.formData,
            { 'Content-Type': 'multipart/form-data' },
            true  // sendHeaders = true
          );
        } else {
          // Regular JSON payload
          return await api.put(
            `${BASE_URL}/license/license_applications/${payload.id}`,
            payload
          );
        }
      },
      {
        onError,
        onSuccess: (response: any) => {
          const data = (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
          onSuccess(data);
        },
      }
    );
    return { updateApplication, error, updating };
  }

// Delete license application
export function useDeleteLicenseApplication(onSuccess?: any, onError?: any) {
  const {
    mutate: deleteApplication,
    error,
    isLoading: deleting,
  } = useMutation(
    ["deleteLicenseApplication"],
    async (id: string) =>
      await api.del(`${BASE_URL}/license/license_applications/${id}`),
    {
      onError,
      onSuccess: (response) => {
        const data = (response || {}).data || {};
        onSuccess(data);
      },
    }
  );
  return { deleteApplication, error, deleting };
}

export function useGetLicenseTypes(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;
  const hasScope = payload && payload.scopes && Object.keys(payload.scopes).length > 0;
  const payloadFilters = (payload && payload.filters) || {};

  const enhancedPayload = (hasCompanies && !hasScope) ? {
    ...(payload || {}),
    include: [
      ...(payload && payload.include ? payload.include : []),
      "company",
      "associate_company",
      "state",
      "city",
    ],
    includes: [
      ...(payload && payload.includes ? payload.includes : []),
      "company",
      "associate_company",
      "state",
      "city",
    ],
    filters: {
      ...payloadFilters,
      ...(payloadFilters.company_id_eq ? {} : { company_id_in: companyIds }),
      // ...(payloadFilters.associate_company_id_eq ? {} : { associate_company_id_in: Array.from(associateCompanyIds) }),
      // ...(payloadFilters.location_id_eq ? {} : { location_id_in: Array.from(locationIds) }),
    }
  } : (payload || {});
  const { data, isFetching, refetch } = useQuery(
    ["licenseTypes", enhancedPayload],
    async () => await api.get(`${BASE_URL}/license/license_types`, enhancedPayload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && (hasCompanies || hasScope),
    }
  );
  var items = (!hasCompanies && !hasScope) ? [] : ((data && data.data && data.data.items) ? data.data.items : []);
  var meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  return {
    licenseTypes: items,
    total: (meta && meta.total_count) || 0,
    isFetching: isFetching || isLoadingCompanies,
    refetch,
    companyIds,
  };
}

export function useCreateLicenseType(onSuccess?: any, onError?: any) {
  const {
    mutate: createLicenseType,
    error,
    isLoading: creating,
  } = useMutation(
    ["createLicenseType"],
    async (payload) =>
      await api.post(`${BASE_URL}/license/license_types`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
      ? response.data
      : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );
  return { createLicenseType, error, creating };
}

export function useUpdateLicenseType(onSuccess?: any, onError?: any) {
  const {
    mutate: updateLicenseType,
    error,
    isLoading: updating,
  } = useMutation(
    ["updateLicenseType"],
    async (payload: any) =>
      await api.put(`${BASE_URL}/license/license_types/${payload.id}`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
      ? response.data
      : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );
  return { updateLicenseType, error, updating };
}

export function useDeleteLicenseType(onSuccess?: any, onError?: any) {
  const {
    mutate: deleteLicenseType,
    error,
    isLoading: deleting,
  } = useMutation(
    ["deleteLicenseType"],
    async (id: string) =>
      await api.del(`${BASE_URL}/license/license_types/${id}`),
    {
      onError,
      onSuccess: (response) => {
        const data = (response || {}).data || {};
        onSuccess(data);
      },
    }
  );
  return { deleteLicenseType, error, deleting };
}

export function useGetCompanyLicenses(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;
  const payloadFilters = (payload && payload.filters) || {};

  const enhancedPayload = hasCompanies ? {
    ...(payload || {}),
    filters: {
      ...payloadFilters,
      ...(payloadFilters.company_id_eq ? {} : { company_id_in: companyIds }),
      // ...(payloadFilters.associate_company_id_eq ? {} : { associate_company_id_in: Array.from(associateCompanyIds) }),
      // ...(payloadFilters.location_id_eq ? {} : { location_id_in: Array.from(locationIds) }),
    }
  } : (payload || {});

  const { data, isFetching, refetch } = useQuery(
    ["companyLicenses", enhancedPayload],
    async () => await api.get(`${BASE_URL}/license/company_licenses`, enhancedPayload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && hasCompanies,
    }
  );

  var items = !hasCompanies ? [] : ((data && data.data && data.data.items) ? data.data.items : []);
  var meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  return {
    licenses: items,
    total: ((meta && meta.total_count) || 0),
    isFetching: isFetching || isLoadingCompanies,
    refetch,
    companyIds,
  };
}

export function useGetCompanyLicense(id: string, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ["companyLicense", id],
    async () => await api.get(`${BASE_URL}/license/company_licenses/${id}`),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!id && enabled,
    }
  );
  return {
    license: (data || {}).data || {},
    isFetching,
    refetch,
  };
}

export function useGetCompanyLicenseCounts(filters: any = {}, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;


  const filtersObject = {
    ...(hasCompanies && !filters.company_id_eq && !filters.company_id_in ? { company_ids: companyIds } : {}),
    ...filters,
  };

  const queryParams = {
    filters: filtersObject,
  };

  const { data, isFetching, refetch } = useQuery(
    ["companyLicenseCounts", queryParams, filters],
    async () => await api.get(`${BASE_URL}/license/company_licenses/counts`, queryParams),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && hasCompanies,
    }
  );

  const counts = (data && data.data) || {
    total_licenses: 0,
    active_licenses: 0,
    perpetual_licenses: 0,
    up_for_renewal: 0,
  };

  return {
    counts,
    isFetching: isFetching || isLoadingCompanies,
    refetch,
  };
}

export function useCreateCompanyLicense(onSuccess?: any, onError?: any) {
  const {
    mutate: createLicense,
    error,
    isLoading: creating,
  } = useMutation(
    ["createCompanyLicense"],
    async (payload: FormData | Record<string, any>) => {
      const isFormData = payload instanceof FormData;
      const response = await api.post(
        `${BASE_URL}/license/company_licenses`,
        payload,
        {
          headers: {
            "Content-Type": isFormData
              ? "multipart/form-data"
              : "application/json",
          },
        }
      );
      // Return only the response body so React Query onSuccess gets consistent shape
      return response.data;
    },
    {
      onError,
      onSuccess: (data: any) => {
        try {
          if (onSuccess) onSuccess(data);
        } catch (err) {
          console.error("Error in onSuccess handler:", err);
        }
      },
    }
  );

  return { createLicense, error, creating };
}

export function useUpdateCompanyLicense(onSuccess?: any, onError?: any) {
  const {
    mutate: updateLicense,
    error,
    isLoading: updating,
  } = useMutation(
    ["updateCompanyLicense"],
    async (payload: any) =>
      await api.put(
        `${BASE_URL}/license/company_licenses/${payload.id}`,
        payload
      ),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
      ? response.data
      : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );
  return { updateLicense, error, updating };
}

export function useDeleteCompanyLicense(onSuccess?: any, onError?: any) {
  const {
    mutate: deleteLicense,
    error,
    isLoading: deleting,
  } = useMutation(
    ["deleteCompanyLicense"],
    async (id: string) =>
      await api.del(`${BASE_URL}/license/company_licenses/${id}`),
    {
      onError,
      onSuccess: (response) => {
        const data = (response || {}).data || {};
        onSuccess(data);
      },
    }
  );
  return { deleteLicense, error, deleting };
}

// Application Types
  export function useGetApplicationTypes(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
      ["applicationTypes", payload],
      async () => await api.get(`${BASE_URL}/license/application_types`, payload),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled,
      }
    );
    var items = (data && data.data && data.data.items) ? data.data.items : [];
    var meta = (data && data.data && data.data.meta) ? data.data.meta : {};
    return {
      applicationTypes: items,
       total: (meta && meta.total_count) || 0,
      isFetching,
      refetch,
    };
  }

// Update Cost Detail
export function useUpdateCostDetail(onSuccess?: any, onError?: any) {

  const {
    mutate: updateCostDetail,
    error,
    isLoading: updating,
  } = useMutation(
    ["updateCostDetail"],
    async (payload: any) => {
      // Determine endpoint based on type: 'company_license' or 'license_application'
      const isCompanyLicense = payload instanceof FormData
        ? payload.get('type') === 'company_license'
        : payload.type === 'company_license';

      const endpoint = isCompanyLicense
        ? `${BASE_URL}/license/company_licenses`
        : `${BASE_URL}/license/license_applications`;

      // Check if this is a file upload (FormData)
      if (payload instanceof FormData) {
        const id = payload.get('id');
        return await api.put(
          `${endpoint}/${id}`,
          payload,
          { 'Content-Type': 'multipart/form-data' },
          true
        );
      } else {
        return await api.put(
          `${endpoint}/${payload.id}`,
          payload
        );
      }
    },
    {
      onError,
      onSuccess: (response: any) => {
        const data = (response && response.status === 200)
          ? response.data
          : (response && response.response && response.response.data);

        onSuccess(data);
      },
    }
  );
  return { updateCostDetail, error, updating };
}

// Get Company License Documents
export function useGetCompanyLicenseDocuments(
    companyLicenseId: string | undefined,
    enabled = true
  ) {
    // First, fetch the CompanyLicense to get license_application_id
    const { data: licenseData, isFetching: fetchingLicense } = useQuery(
      ["companyLicense", companyLicenseId],
      async () => await api.get(`${BASE_URL}/license/company_licenses/${companyLicenseId}`, {
        include: ["issued_document"],
        includes: ["issued_document"]
      }),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!companyLicenseId,
      }
    );

    const licenseApplicationId = licenseData && licenseData.data && licenseData.data.license_application_id;

    // Then fetch license application documents using the license_application_id
    const payload = {
      filters: {
        license_application_id_eq: licenseApplicationId
      },
      include: ["application_type_document", "application_type_document.document_type", "asset"],
      includes: ["application_type_document", "application_type_document.document_type", "asset"],
    };

    const { data, isFetching: fetchingDocs, refetch } = useQuery(
      ["companyLicenseDocuments", companyLicenseId, licenseApplicationId],
      async () => await api.get(`${BASE_URL}/license/license_application_documents`, payload),
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !!licenseApplicationId,
      }
    );

    var items = (data && data.data && data.data.items) ? data.data.items : [];
    var meta = (data && data.data && data.data.meta) ? data.data.meta : {};

    // Include the issued document (final license file) as a special document
    const issuedDoc = licenseData && licenseData.data && licenseData.data.issued_document;
    const allDocuments = issuedDoc ? [...items, {
      id: `issued-doc-${companyLicenseId}`,
      document_type_name: "Final License Document",
      asset: issuedDoc,
      is_issued_document: true // Flag to identify this special document
    }] : items;

    return {
      companyLicenseDocuments: allDocuments,
      total: (meta && meta.total_count) || 0,
      isFetching: fetchingLicense || fetchingDocs,
      refetch,
    };
  }
